import { createApiContext, ensurePackageBundled, type PartialContext } from '@ms-cloudpack/api-server';
import { writeJson } from '@ms-cloudpack/json-utilities';
import { makeUrl } from '@ms-cloudpack/path-string-parsing';
import { yellow } from '@ms-cloudpack/task-reporter';
import path from 'path';
import type { CommandExitParams, CommandAction } from '../../types/CommandAction.js';
import { runPrerequisites } from '../../utilities/runPrerequisites.js';
import { openBrowser } from './openBrowser.js';
import { trackSession } from './trackSession.js';
import type { StartOptions } from './types/StartOptions.js';
import { getCliStartTime } from '../../utilities/getCliStartTime.js';
import { createApiServer } from '../../utilities/createApiServer.js';
import { resolveCloudpackInternalPath } from '../../utilities/resolveCloudpackInternalPath.js';
import { getDomain, parseHttpsConfig } from '@ms-cloudpack/create-express-app';
import { handleKnownParseHttpsErrors } from '../../utilities/handleKnownParseHttpsErrors.js';
import { createWatcher } from '@ms-cloudpack/file-watcher';
import { readConfig } from '@ms-cloudpack/config';
import { disposeServer } from '../../utilities/disposeServer.js';

/**
 * Defines the "start" verb entry point.
 */
export const execute: CommandAction<StartOptions> = async (params) => {
  const { appPath, config, options, autoDispose, isRestart } = params;

  console.log('App path:', yellow(appPath));
  console.log('Bundle mode:', yellow(options.mode));

  await runPrerequisites(params);

  // Auto-run init with static analysis before start. Since static analysis is fast (~22s),
  // always run it to ensure the generated config is up to date with current dependencies.
  if (config.features?.staticAnalysisInit) {
    const { init } = await import('../init/init.js');
    const { InitSummaryData } = await import('../init/InitSummaryData.js');
    const { formatInitSummary } = await import('../init/formatInitSummary.js');

    config.generated = {};
    console.log('Running init with static analysis...\n');

    const summaryData = new InitSummaryData({ appPath });
    await init({
      appPath: params.appPath,
      cachePath: params.cachePath,
      config: params.config,
      reporter: params.reporter,
      taskRunner: params.taskRunner,
      telemetryClient: params.telemetryClient,
      options: { cache: options.cache },
      skipWrite: true,
      isMultiApp: false,
      summaryData,
    });

    const summary = summaryData.summarize();
    console.log(formatInitSummary({ summary, check: false, isMultiApp: false, verb: 'init' }));
  }

  // If the user has specified https options in the config, parse them at start.
  // This makes sure that the HTTPS configuration is ready before starting the servers and shows errors early.
  // We need to do this after prerequisites are run, in case users setup their certs there.
  if (config?.server?.https) {
    try {
      const domain = getDomain(config.server.domain);
      config.server.parsedHttps = await parseHttpsConfig({ domain, https: config.server.https, cwd: appPath });
    } catch (err) {
      handleKnownParseHttpsErrors({ err, projectName: path.basename(appPath) });
      throw err;
    }
  }

  const overlayPath = await resolveCloudpackInternalPath({
    packageName: '@ms-cloudpack/overlay',
    importMetaUrl: import.meta.url,
  });

  const shouldUseRemoteCache = config.features?.enableCloudHosted || config.features?.enableRemoteCacheDownloads;
  const apiContext = autoDispose(
    await createApiContext({
      ...params,
      remoteCacheClientLoginMethod: shouldUseRemoteCache ? options.login : undefined,
      additionalPaths: overlayPath ? [overlayPath] : undefined,
    }),
  );
  const { packages, session, telemetryClient } = apiContext;

  return new Promise<CommandExitParams>((resolve) => {
    (async () => {
      let hasConfigChanged = false;
      const configWatcher = createWatcher();
      await Promise.all(
        config.configPaths?.appConfigPaths.map(
          (configPath) =>
            configWatcher.watch({ path: path.basename(configPath), watchPaths: [configPath] }, () => {
              if (hasConfigChanged) {
                return;
              }
              void readConfig(appPath)
                .then(() => {
                  hasConfigChanged = true;
                  resolve({ restart: true, message: 'Config changed. Restarting Cloudpack...' });
                })
                .catch((error) => console.warn('Config change detected, but the config file is invalid.', error));
            }) ?? [],
        ) ?? [],
      );

      // Set up config watcher disposal (up here in case the config changes before setup completes).
      autoDispose(configWatcher);

      // Increment the session version if caching is disabled.
      if (options.cache === false) {
        session.incrementSessionVersion();
      }

      // Make sure the package has a valid definition.
      const definition = await packages.get(appPath);

      if (options.logResolveMap) {
        // Write the resolve map to disk.
        await writeJson(path.join(appPath, 'resolve-map.json'), session.resolveMap);
      }

      // Start api server for tracking status and handling remote requests.
      const apiServerResult = await createApiServer({ port: options.apiServerPort }, apiContext);
      if (apiServerResult.hasErrors) {
        return apiServerResult;
      }
      const { apiServer } = apiServerResult;
      autoDispose({ dispose: () => disposeServer('API server', apiServer) });

      // Kick off bundling app package as soon as api server is ready.
      void ensurePackageBundled(
        {
          name: definition.name,
          version: definition.version,
          shouldGetBundleInfo: options.logBundleInfo,
        },
        apiContext,
      );

      // Then start bundle and app servers for hosting the app.
      const { startServers } = await import('@ms-cloudpack/app-server');
      const servers = await startServers(
        {
          definition,
          bundleServerOptions: { disableCache: !options.cache, port: options.bundleServerPort },
          port: options.appServerPort,
        },
        apiContext,
      );
      autoDispose({ dispose: () => disposeServer('app server', servers.appServer) });
      autoDispose({ dispose: () => disposeServer('bundle server', servers.bundleServer) });

      // Save the session to active sessions.
      const sessionTracker = await trackSession(session);
      autoDispose({ dispose: () => sessionTracker.close() });

      // Log the time elapsed from when the CLI started until the browser is ready to be opened.
      await telemetryClient.tracer.startActiveSpan(
        'TIME_TO_OPEN_BROWSER',
        { startTime: getCliStartTime(), attributes: { open: options.open } },
        () => {
          if (!isRestart) {
            handleOpenBrowser(options, apiContext);
          }
        },
      );

      params.commandEvents.emit('ready');
    })().catch((err) => {
      console.error('Error in start command:', err);
      resolve({ hasErrors: true, message: `Error in start command: ${err}` });
    });
  });
};

function handleOpenBrowser(
  options: StartOptions,
  apiContext: PartialContext<'telemetryClient', 'config' | 'urls'>,
): void {
  const { config, urls } = apiContext.session;

  const defaultUrl = makeUrl(config.server?.defaultPath || '', urls.appServer).href;

  // Open the browser.
  if (options.open === false) {
    console.log('Please open the browser manually to:', defaultUrl);
  } else {
    let urlToOpen: string;
    if (typeof options.open === 'string') {
      // If the string doesn't start with http or https, pick a protocol based on config.server.https
      if (!options.open.startsWith('http://') && !options.open.startsWith('https://')) {
        urlToOpen = `${config.server?.https ? 'https' : 'http'}://${options.open}`;
      } else {
        urlToOpen = options.open;
      }
    } else {
      // Use the default path from the config with the first domain.
      urlToOpen = defaultUrl;
    }

    void openBrowser({ url: urlToOpen }, apiContext).catch((err: unknown) => {
      console.warn('Error opening browser:', (err as Error | undefined)?.stack || err);
      console.warn('Please open the browser manually to:', defaultUrl);
    });
  }
}

// cspell:ignore Prereqs
